import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

export function useCrispSupport() {
    const location = useLocation();

    const [supportOpen, setSupportOpen] = useState(false);
    const crispInitializedRef = useRef(false);

    // --- Layout --- //
    const applyCrispPanelLayout = useCallback(() => {
        const crispPanel = document.querySelector(
            '.crisp-client [data-visible="true"][data-chat-status][style*="width"]'
        ) as HTMLElement | null;

        if (!crispPanel) return;

        const isMobile = window.innerWidth <= 767;
        const isTablet = window.innerWidth <= 1023;

        if (isMobile) {
            crispPanel.style.setProperty("right", "0", "important");
            crispPanel.style.setProperty("left", "0", "important");
            crispPanel.style.setProperty("bottom", "0", "important");
            crispPanel.style.setProperty("width", "100%", "important");
            crispPanel.style.setProperty("height", `${window.innerHeight - 120}px`, "important");
            crispPanel.style.removeProperty("top");
        } else if (isTablet) {
            crispPanel.style.setProperty("right", "10px", "important");
            crispPanel.style.setProperty("left", "unset", "important");
            crispPanel.style.setProperty("bottom", "10px", "important");
            crispPanel.style.setProperty("width", "360px", "important");
            crispPanel.style.setProperty("height", `${window.innerHeight - 80}px`, "important");
            crispPanel.style.removeProperty("top");
        } else {
            crispPanel.style.setProperty("right", "10px", "important");
            crispPanel.style.setProperty("left", "unset", "important");
            crispPanel.style.setProperty("bottom", "10px", "important");
            crispPanel.style.setProperty("width", "600px", "important");
            crispPanel.style.setProperty("height", `${window.innerHeight - 80}px`, "important");
            crispPanel.style.removeProperty("top");
        }

        crispPanel.style.setProperty("max-height", "none", "important");
        crispPanel.style.setProperty("min-height", "0", "important");
        crispPanel.style.setProperty("overflow", "hidden", "important");
    }, []);

    // --- Hide launcher --- //
    const hideCrispLauncher = useCallback(() => {
        document
            .querySelectorAll('.crisp-client [data-id="chat_opened"], .crisp-client [data-id="chat_closed"]')
            .forEach((el) => {
                const wrapper = el.parentElement?.parentElement as HTMLElement | null;
                if (!wrapper) return;

                wrapper.style.setProperty("display", "none", "important");
                wrapper.style.setProperty("pointer-events", "none", "important");
            });
    }, []);

    // --- Hide quick actions --- //
    const hideCrispQuickActions = useCallback(() => {
        const labelsToHide = ["Quick Actions", "Hurtige handlinger"];

        document
            .querySelectorAll('.crisp-client [aria-label]')
            .forEach((el) => {
                const label = el.getAttribute("aria-label");
                if (label && labelsToHide.includes(label)) {
                    const button = el as HTMLElement;

                    button.style.setProperty("display", "none", "important");
                    button.style.setProperty("visibility", "hidden", "important");
                    button.style.setProperty("pointer-events", "none", "important");
                }
            });
    }, []);

    // --- Sync --- //
    const syncCrispUi = useCallback(() => {
        const crispPanel = document.querySelector(
            '.crisp-client [data-visible="true"][data-chat-status][style*="width"]'
        ) as HTMLElement | null;

        if (crispPanel) {
            crispPanel.style.setProperty("opacity", "0", "important");
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                hideCrispLauncher();
                hideCrispQuickActions();
                applyCrispPanelLayout();

                const crispPanel = document.querySelector(
                    '.crisp-client [data-visible="true"][data-chat-status][style*="width"]'
                ) as HTMLElement | null;

                if (crispPanel) {
                    crispPanel.style.setProperty("opacity", "1", "important");
                }
            });
        });
    }, [hideCrispLauncher, hideCrispQuickActions, applyCrispPanelLayout]);

    const runCrispLayoutSync = useCallback(() => {
        syncCrispUi();
        requestAnimationFrame(() => syncCrispUi());
    }, [syncCrispUi]);

    // --- Init --- //
    useEffect(() => {
        if (crispInitializedRef.current) return;

        try {
            window.$crisp?.push(["do", "chat:hide"]);

            const styleId = "crisp-no-animation";
            if (!document.getElementById(styleId)) {
                const style = document.createElement("style");
                style.id = styleId;
                style.innerHTML = `
               .crisp-client [data-visible="true"][data-chat-status][style*="width"] {
                    transition: none !important;
                    animation: none !important;
               }
            `;
                document.head.appendChild(style);
            }

            crispInitializedRef.current = true;
        } catch (e) {
            console.error(e);
        }
    }, []);

    // --- Route change --- //
    useEffect(() => {
        runCrispLayoutSync();
    }, [location.pathname, runCrispLayoutSync]);

    // --- Resize --- //
    useEffect(() => {
        if (!supportOpen) return;

        let frame: number | null = null;

        const handleResize = () => {
            if (frame) cancelAnimationFrame(frame);

            frame = requestAnimationFrame(() => {
                applyCrispPanelLayout();
                frame = null;
            });
        };

        window.addEventListener("resize", handleResize);
        window.visualViewport?.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            window.visualViewport?.removeEventListener("resize", handleResize);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [supportOpen, applyCrispPanelLayout]);

    // --- Toggle --- //
    const openSupportChat = useCallback(() => {
        try {
            window.$crisp?.push(["do", "chat:show"]);
            window.$crisp?.push(["do", "chat:open"]);
            setSupportOpen(true);
            syncCrispUi();
        } catch (e) {
            console.error(e);
        }
    }, [syncCrispUi]);

    const closeSupportChat = useCallback(() => {
        try {
            window.$crisp?.push(["do", "chat:close"]);
            window.$crisp?.push(["do", "chat:hide"]);
            setSupportOpen(false);
            syncCrispUi();
        } catch (e) {
            console.error(e);
        }
    }, [syncCrispUi]);

    const toggleSupportChat = useCallback(() => {
        if (supportOpen) {
            closeSupportChat();
        } else {
            openSupportChat();
        }
    }, [supportOpen, openSupportChat, closeSupportChat]);

    return {
        supportOpen,
        openSupportChat,
        closeSupportChat,
        toggleSupportChat
    };
}